import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Hand, Eye, Pause, Activity, Focus, Move, Brain, BookOpen,
  Heart, Users, CheckCircle, History, ChevronDown, ChevronUp,
  Plus, Trash2, ArrowLeft, AlertCircle, Sparkles,
} from "lucide-react";
import { API } from "../App";
import ShareExerciseDialog from "../components/ShareExerciseDialog";
import { useSharePrompt } from "../hooks/useSharePrompt";

/* ── Exercise definitions ─────────────────────── */

const BODY_ZONES = [
  "Mains", "Bras", "Poitrine", "Ventre", "Cuisses", "Cou", "Visage", "Dos", "Pieds",
];

const EXERCISES = [
  {
    id: "sex_sensory_mapping",
    num: 1,
    name: "Cartographie sensorielle non genitale",
    description: "10-15 min, 3 fois/semaine. Explorer les sensations corporelles non genitales (mains, bras, poitrine, ventre, cuisses, cou, visage). But : elargir le repertoire sensoriel et reduire l'hyperfocalisation sur le symptome sexuel.",
    icon: Hand,
    color: "bg-rose-50 text-rose-600 dark:bg-rose-950/30 dark:text-rose-400",
    formType: "table",
  },
  {
    id: "sex_neutral_scan",
    num: 2,
    name: "Scan de plaisir neutre",
    description: "Explorer le corps zone par zone en classant chaque sensation dans des categories descriptives : chaud/froid, lourd/leger, tension/relachement, doux/rugueux, pulsation/calme. But : enrichir le vocabulaire sensoriel sans jugement sexuel.",
    icon: Eye,
    color: "bg-violet-50 text-violet-600 dark:bg-violet-950/30 dark:text-violet-400",
    formType: "prompts",
    prompts: [
      { key: "zone_explored", label: "Zone du corps exploree", placeholder: "Ex : ventre, interieur des bras, nuque..." },
      { key: "hot_cold", label: "Chaud / Froid", placeholder: "Quelles sensations thermiques avez-vous percues ?" },
      { key: "heavy_light", label: "Lourd / Leger", placeholder: "Poids ressenti dans cette zone..." },
      { key: "tension_release", label: "Tension / Relachement", placeholder: "Notez les zones de tension ou de detente..." },
      { key: "soft_rough", label: "Doux / Rugueux", placeholder: "Qualite de la texture percue..." },
      { key: "pulse_calm", label: "Pulsation / Calme", placeholder: "Rythme, vibrations, immobilite..." },
      { key: "reflection", label: "Observation generale", placeholder: "Que retenez-vous de cette exploration ? Un mot pour resumer ?" },
    ],
  },
  {
    id: "sex_stop_rule",
    num: 3,
    name: "Masturbation avec regle d'arret",
    description: "Quand le patient remarque une pensee de performance, il s'arrete 20-30 secondes, respire, nomme la pensee avec la formule ACT (\"Je remarque que j'ai la pensee que...\"), puis revient au contact sensoriel.",
    icon: Pause,
    color: "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400",
    formType: "prompts",
    prompts: [
      { key: "performance_thought", label: "Pensee de performance identifiee", placeholder: "Ex : 'Il faut que je sois excite(e)', 'Ca ne va pas marcher'..." },
      { key: "defusion_formula", label: "Formule de defusion ACT", placeholder: "Je remarque que j'ai la pensee que..." },
      { key: "sensory_return", label: "Retour au contact sensoriel", placeholder: "Vers quelle sensation etes-vous revenu(e) ? Qu'avez-vous remarque ?" },
      { key: "stop_count", label: "Nombre d'arrets durant la seance", placeholder: "Ex : 3" },
      { key: "reflection", label: "Reflexion apres l'exercice", placeholder: "Comment avez-vous vecu l'exercice ? Qu'avez-vous appris ?" },
    ],
  },
  {
    id: "sex_touch_pause",
    num: 4,
    name: "Alternance toucher-pause-retour",
    description: "1 min de stimulation douce, 30 sec d'arret complet, 1 min d'attention aux sensations residuelles. Reprise uniquement si le patient reste en mode exploration. But : couper le reflexe de performance lineaire.",
    icon: Activity,
    color: "bg-cyan-50 text-cyan-600 dark:bg-cyan-950/30 dark:text-cyan-400",
    formType: "prompts",
    prompts: [
      { key: "phase1_sensations", label: "Phase 1 — Stimulation douce (1 min)", placeholder: "Sensations percues durant le toucher..." },
      { key: "phase2_pause", label: "Phase 2 — Arret complet (30 sec)", placeholder: "Que se passe-t-il quand vous arretez ? Pensees, sensations residuelles..." },
      { key: "phase3_residual", label: "Phase 3 — Attention aux sensations residuelles (1 min)", placeholder: "Quelles sensations persistent ? Ont-elles change de nature ?" },
      { key: "resume_decision", label: "Decision de reprise", placeholder: "Avez-vous repris ? En mode exploration ou performance ?" },
      { key: "reflection", label: "Reflexion", placeholder: "Qu'avez-vous observe sur votre fonctionnement ?" },
    ],
  },
  {
    id: "sex_sensory_channels",
    num: 5,
    name: "Focalisation sur trois canaux sensoriels",
    description: "Pendant l'exercice, le patient alterne volontairement trois focalisations : tactile (texture, pression, temperature), interne (chaleur, tension, rythme respiratoire, bassin) et emotionnelle (gene, peur, plaisir, impatience, curiosite). Il observe, nomme et revient a l'experience.",
    icon: Focus,
    color: "bg-teal-50 text-teal-600 dark:bg-teal-950/30 dark:text-teal-400",
    formType: "prompts",
    prompts: [
      { key: "tactile", label: "Canal tactile (texture, pression, temperature)", placeholder: "Qu'avez-vous percu au niveau du toucher ?" },
      { key: "internal", label: "Canal interne (chaleur, tension, rythme respiratoire, bassin)", placeholder: "Sensations internes observees..." },
      { key: "emotional", label: "Canal emotionnel (gene, peur, plaisir, impatience, curiosite)", placeholder: "Quelles emotions sont apparues ?" },
      { key: "alternation_ease", label: "Facilite a alterner entre les canaux", placeholder: "Etait-ce facile ou difficile de passer d'un canal a l'autre ?" },
      { key: "reflection", label: "Observation : nommer sans modifier", placeholder: "Avez-vous reussi a observer sans chercher a modifier ? Qu'avez-vous appris ?" },
    ],
  },
  {
    id: "sex_pelvic_movement",
    num: 6,
    name: "Mouvements du bassin gradues",
    description: "Progression en 4 niveaux pour reintroduire la conscience du bassin sans objectif de performance. A : allonge, bascules lentes. B : assis, cercles du bassin. C : debout, micro-mouvements. D : integration dans un contact sexuel non penetratif.",
    icon: Move,
    color: "bg-indigo-50 text-indigo-600 dark:bg-indigo-950/30 dark:text-indigo-400",
    formType: "levels",
  },
  {
    id: "sex_performance_thoughts",
    num: 7,
    name: "Exposition aux pensees de performance",
    description: "Le patient identifie ses pensees automatiques de performance (\"il faut que...\", \"je devrais...\") et applique des techniques de defusion ACT : repeter la pensee avec une voix de dessin anime, prefixer avec \"Je remarque que j'ai la pensee que...\"",
    icon: Brain,
    color: "bg-orange-50 text-orange-600 dark:bg-orange-950/30 dark:text-orange-400",
    formType: "prompts",
    prompts: [
      { key: "thought1", label: "Pensee automatique 1", placeholder: "Ex : Il faut que je sois performant(e)..." },
      { key: "defusion1", label: "Reformulation defusion ACT", placeholder: "Je remarque que j'ai la pensee que..." },
      { key: "thought2", label: "Pensee automatique 2 (optionnel)", placeholder: "Ex : Je devrais ressentir du plaisir..." },
      { key: "defusion2", label: "Reformulation defusion ACT", placeholder: "Je remarque que j'ai la pensee que..." },
      { key: "thought3", label: "Pensee automatique 3 (optionnel)", placeholder: "Autre pensee de performance..." },
      { key: "defusion3", label: "Reformulation defusion ACT", placeholder: "Je remarque que j'ai la pensee que..." },
      { key: "effect", label: "Effet observe apres la defusion", placeholder: "La pensee a-t-elle perdu de son pouvoir ? Qu'avez-vous remarque ?" },
    ],
  },
  {
    id: "sex_vitality_journal",
    num: 8,
    name: "Journal vitalite versus souffrance",
    description: "Le patient note ses comportements sexuels dans un tableau a trois colonnes : situation/sensation, vers la vitalite (comportements alignes avec ses valeurs) et vers la souffrance (evitement, performance, rigidite).",
    icon: BookOpen,
    color: "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400",
    formType: "journal",
  },
  {
    id: "sex_sensate_journal",
    num: 9,
    name: "Carnet de focalisation sensorielle",
    description: "Apres chaque pratique de focalisation sensorielle, completez ce carnet. Ce n'est pas un outil de surveillance, mais de comprehension : il vous aide a mieux saisir le fonctionnement du cercle anxiete-performance dans votre cas particulier.",
    icon: BookOpen,
    color: "bg-indigo-50 text-indigo-600 dark:bg-indigo-950/30 dark:text-indigo-400",
    formType: "prompts",
    prompts: [
      { key: "practice_date", label: "Date de la pratique", placeholder: "Ex : 15 fevrier 2026" },
      { key: "practiced", label: "Avez-vous pratique aujourd'hui ?", placeholder: "Oui / Non — si non, qu'est-ce qui s'est passe ?" },
      { key: "duration", label: "Duree approximative (en minutes)", placeholder: "Ex : 15" },
      { key: "anxiety_start", label: "Niveau d'anxiete au debut (0-10)", placeholder: "0 = aucune, 10 = maximale" },
      { key: "anxiety_end", label: "Niveau d'anxiete a la fin (0-10)", placeholder: "0 = aucune, 10 = maximale" },
      { key: "main_thoughts", label: "Pensees principales", placeholder: "Ex : « il faut que ca marche », « je dois reussir », « j'ai peur de perdre l'erection »..." },
      { key: "sensations_observed", label: "Sensations observees", placeholder: "Chaleur, pression, rythme, tension, respiration, mouvements du bassin, sensations dans les cuisses..." },
      { key: "checked_or_forced", label: "Ai-je verifie ou force pendant l'exercice ?", placeholder: "Ex : « j'ai surveille mon erection a 3 reprises », « j'ai accelere pour tenir », « je suis reste dans l'exploration »..." },
      { key: "learning", label: "Qu'ai-je appris ?", placeholder: "Une observation, un signal corporel precurseur, un geste qui aide a revenir aux sensations, une regularite..." },
    ],
  },
  {
    id: "sex_couple_visual_contact",
    num: "10A",
    name: "Contact visuel et respiration synchronisee",
    description: "En couple. 2-3 minutes, assis face a face, mains en contact, sans parler. Objectif : securite relationnelle, presence, baisse de la pression sexuelle.",
    icon: Heart,
    color: "bg-pink-50 text-pink-600 dark:bg-pink-950/30 dark:text-pink-400",
    isCouple: true,
    formType: "prompts",
    prompts: [
      { key: "duration", label: "Duree de l'exercice (en minutes)", placeholder: "Ex : 3" },
      { key: "feelings_during", label: "Ressenti pendant l'exercice", placeholder: "Qu'avez-vous ressenti pendant le contact visuel et la respiration ?" },
      { key: "comfort_level", label: "Niveau de confort (1-10)", placeholder: "1 = tres inconfortable, 10 = tres a l'aise" },
      { key: "partner_feedback", label: "Retour du/de la partenaire (si partage)", placeholder: "Qu'a dit ou ressenti votre partenaire ?" },
      { key: "reflection", label: "Reflexion", placeholder: "Qu'avez-vous appris sur votre rapport a l'intimite ?" },
    ],
  },
  {
    id: "sex_couple_nonsexual_touch",
    num: "10B",
    name: "Toucher non sexuel a tour de role",
    description: "En couple. 10 min chacun. Celui qui recoit guide avec des mots simples : 'plus lent', 'plus leger', 'pause', 'continue'. Aucun rapport, aucune stimulation genitale, aucun objectif d'excitation.",
    icon: Users,
    color: "bg-fuchsia-50 text-fuchsia-600 dark:bg-fuchsia-950/30 dark:text-fuchsia-400",
    isCouple: true,
    formType: "prompts",
    prompts: [
      { key: "role", label: "Votre role (donneur ou receveur d'abord ?)", placeholder: "Ex : receveur puis donneur" },
      { key: "words_used", label: "Mots de guidage utilises", placeholder: "Ex : plus lent, pause, continue, plus doux..." },
      { key: "receiver_experience", label: "Ressenti en tant que receveur", placeholder: "Qu'avez-vous percu ? Etait-ce facile de guider ?" },
      { key: "giver_experience", label: "Ressenti en tant que donneur", placeholder: "Comment avez-vous vecu le fait d'etre guide ?" },
      { key: "reflection", label: "Reflexion commune", placeholder: "Qu'avez-vous decouvert sur votre communication corporelle ?" },
    ],
  },
  {
    id: "sex_couple_sensate_focus",
    num: "10C",
    name: "Sensate focus : zones autorisees",
    description: "En couple. Progression sur 4 semaines. Sem. 1 : mains, bras, dos, visage. Sem. 2 : torse, ventre, jambes. Sem. 3 : proximite intime sans penetration. Sem. 4 : reprise eventuelle. Le couple doit reussir l'exercice, pas reussir un rapport.",
    icon: Sparkles,
    color: "bg-purple-50 text-purple-600 dark:bg-purple-950/30 dark:text-purple-400",
    isCouple: true,
    formType: "sensate",
  },
];

/* ── Table form (Exercise 1) ─────────────────── */

const TableForm = ({ rows, setRows }) => {
  const addRow = () => setRows([...rows, { zone: "", sensations: "", intensity: "", thoughts: "" }]);
  const updateRow = (i, key, val) => {
    const next = [...rows];
    next[i] = { ...next[i], [key]: val };
    setRows(next);
  };
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      <div className="hidden sm:grid sm:grid-cols-[1fr_1.2fr_80px_1fr_36px] gap-2 text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-1">
        <span>Zone</span><span>Sensations</span><span>Intensite</span><span>Pensees</span><span />
      </div>
      {rows.map((row, i) => (
        <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1.2fr_80px_1fr_36px] gap-2 p-2 rounded-lg bg-muted/30 border border-border/30" data-testid={`table-row-${i}`}>
          <div>
            <label className="text-[10px] text-muted-foreground sm:hidden">Zone</label>
            <select
              value={row.zone}
              onChange={(e) => updateRow(i, "zone", e.target.value)}
              className="w-full h-9 rounded-md border bg-background px-2 text-sm"
            >
              <option value="">Choisir...</option>
              {BODY_ZONES.map(z => <option key={z} value={z}>{z}</option>)}
              <option value="Autre">Autre</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground sm:hidden">Sensations</label>
            <Input
              value={row.sensations}
              onChange={(e) => updateRow(i, "sensations", e.target.value)}
              placeholder="chaleur, tension..."
              className="text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground sm:hidden">Intensite</label>
            <Input
              type="number" min="0" max="10"
              value={row.intensity}
              onChange={(e) => updateRow(i, "intensity", e.target.value)}
              placeholder="0-10"
              className="text-sm"
            />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground sm:hidden">Pensees</label>
            <Input
              value={row.thoughts}
              onChange={(e) => updateRow(i, "thoughts", e.target.value)}
              placeholder="Ce que j'ai pense..."
              className="text-sm"
            />
          </div>
          <Button variant="ghost" size="icon" className="h-9 w-9 text-muted-foreground hover:text-red-500" onClick={() => removeRow(i)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addRow} data-testid="add-table-row">
        <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter une zone
      </Button>
    </div>
  );
};

/* ── Levels form (Exercise 6 - Pelvic) ──────── */

const PELVIC_LEVELS = [
  { id: "A", label: "Niveau A", desc: "Allonge(e), bascules lentes du bassin, respiration libre" },
  { id: "B", label: "Niveau B", desc: "Assis(e), cercles lents du bassin, attention au centre du corps" },
  { id: "C", label: "Niveau C", desc: "Debout, micro-mouvements du bassin, ancrage au sol" },
  { id: "D", label: "Niveau D", desc: "Integration dans un contact non penetratif, maintien de l'exploration" },
];

const LevelsForm = ({ responses, setResponses }) => (
  <div className="space-y-4">
    <div className="p-3 rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200/40 text-sm text-blue-800 dark:text-blue-200">
      <AlertCircle className="h-4 w-4 inline mr-2" />
      Progressez a votre rythme. Ne passez au niveau suivant que lorsque le precedent est confortable.
    </div>
    {PELVIC_LEVELS.map((level) => (
      <Card key={level.id} className="border-border/30" data-testid={`level-${level.id}`}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">{level.label}</Badge>
            <span className="text-sm text-muted-foreground">{level.desc}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium">Sensations observees</label>
              <Textarea
                value={responses[`${level.id}_sensations`] || ""}
                onChange={(e) => setResponses(p => ({ ...p, [`${level.id}_sensations`]: e.target.value }))}
                placeholder="Qu'avez-vous ressenti ?"
                rows={2}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-xs font-medium">Confort (1-10)</label>
              <Input
                type="number" min="1" max="10"
                value={responses[`${level.id}_comfort`] || ""}
                onChange={(e) => setResponses(p => ({ ...p, [`${level.id}_comfort`]: e.target.value }))}
                placeholder="1-10"
                className="mt-1"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    ))}
    <div>
      <label className="text-xs font-medium">Reflexion generale</label>
      <Textarea
        value={responses.reflection || ""}
        onChange={(e) => setResponses(p => ({ ...p, reflection: e.target.value }))}
        placeholder="Qu'avez-vous appris sur votre rapport au bassin et au mouvement ?"
        rows={3}
        className="mt-1"
      />
    </div>
  </div>
);

/* ── Journal form (Exercise 8 - Vitality) ───── */

const JournalForm = ({ rows, setRows }) => {
  const addRow = () => setRows([...rows, { situation: "", vitality: "", suffering: "" }]);
  const updateRow = (i, key, val) => {
    const next = [...rows];
    next[i] = { ...next[i], [key]: val };
    setRows(next);
  };
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      <div className="p-3 rounded-lg bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200/40 text-sm text-emerald-800 dark:text-emerald-200">
        <AlertCircle className="h-4 w-4 inline mr-2" />
        Pour chaque situation, identifiez les comportements qui vous rapprochent de vos valeurs (vitalite) et ceux qui vous en eloignent (souffrance).
      </div>
      {rows.map((row, i) => (
        <Card key={i} className="border-border/30" data-testid={`journal-row-${i}`}>
          <CardContent className="p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Entree {i + 1}</span>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-red-500" onClick={() => removeRow(i)}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
            <Textarea
              value={row.situation}
              onChange={(e) => updateRow(i, "situation", e.target.value)}
              placeholder="Situation / sensation vecue..."
              rows={2}
              className="text-sm"
            />
            <div className="grid sm:grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-medium text-emerald-600">Vers la vitalite</label>
                <Textarea
                  value={row.vitality}
                  onChange={(e) => updateRow(i, "vitality", e.target.value)}
                  placeholder="Comportements alignes avec mes valeurs..."
                  rows={2}
                  className="text-sm border-emerald-200/50"
                />
              </div>
              <div>
                <label className="text-[10px] font-medium text-red-500">Vers la souffrance</label>
                <Textarea
                  value={row.suffering}
                  onChange={(e) => updateRow(i, "suffering", e.target.value)}
                  placeholder="Evitement, performance, rigidite..."
                  rows={2}
                  className="text-sm border-red-200/50"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      <Button variant="outline" size="sm" onClick={addRow} data-testid="add-journal-row">
        <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter une entree
      </Button>
    </div>
  );
};

/* ── Sensate Focus form (Exercise 10C) ──────── */

const SENSATE_WEEKS = [
  { id: "1", label: "Semaine 1", zones: "Mains, bras, dos, visage, cheveux" },
  { id: "2", label: "Semaine 2", zones: "Torse, ventre, jambes" },
  { id: "3", label: "Semaine 3", zones: "Proximite corporelle plus intime, sans penetration ni objectif orgasmique" },
  { id: "4", label: "Semaine 4", zones: "Reprise eventuelle d'une sexualite plus complete, si attitude d'exploration maintenue" },
];

const SensateForm = ({ responses, setResponses }) => (
  <div className="space-y-4">
    <div className="p-3 rounded-lg bg-purple-50/50 dark:bg-purple-950/20 border border-purple-200/40 text-sm text-purple-800 dark:text-purple-200">
      <AlertCircle className="h-4 w-4 inline mr-2" />
      Le couple doit reussir l'exercice, pas reussir un rapport. Remplissez la semaine en cours.
    </div>
    <div>
      <label className="text-xs font-medium">Semaine en cours</label>
      <select
        value={responses.current_week || ""}
        onChange={(e) => setResponses(p => ({ ...p, current_week: e.target.value }))}
        className="w-full h-9 rounded-md border bg-background px-2 text-sm mt-1"
      >
        <option value="">Choisir...</option>
        {SENSATE_WEEKS.map(w => <option key={w.id} value={w.id}>{w.label} — {w.zones}</option>)}
      </select>
    </div>
    <div>
      <label className="text-xs font-medium">Zones explorees</label>
      <Textarea
        value={responses.zones_explored || ""}
        onChange={(e) => setResponses(p => ({ ...p, zones_explored: e.target.value }))}
        placeholder="Quelles zones avez-vous explorees ?"
        rows={2}
        className="mt-1"
      />
    </div>
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <label className="text-xs font-medium">Mon ressenti</label>
        <Textarea
          value={responses.my_feeling || ""}
          onChange={(e) => setResponses(p => ({ ...p, my_feeling: e.target.value }))}
          placeholder="Comment avez-vous vecu l'exercice ?"
          rows={3}
          className="mt-1"
        />
      </div>
      <div>
        <label className="text-xs font-medium">Ressenti du/de la partenaire</label>
        <Textarea
          value={responses.partner_feeling || ""}
          onChange={(e) => setResponses(p => ({ ...p, partner_feeling: e.target.value }))}
          placeholder="Comment votre partenaire a-t-il/elle vecu l'exercice ?"
          rows={3}
          className="mt-1"
        />
      </div>
    </div>
    <div>
      <label className="text-xs font-medium">Attitude maintenue ? (exploration vs performance)</label>
      <Textarea
        value={responses.attitude || ""}
        onChange={(e) => setResponses(p => ({ ...p, attitude: e.target.value }))}
        placeholder="Avez-vous reussi a rester en mode exploration ? Si non, qu'est-ce qui a bascule ?"
        rows={2}
        className="mt-1"
      />
    </div>
  </div>
);

/* ── Prompts form (generic textareas) ────────── */

const PromptsForm = ({ prompts, responses, setResponses }) => (
  <div className="space-y-4">
    {prompts.map((prompt) => (
      <div key={prompt.key} className="space-y-1.5">
        <label className="text-sm font-medium">{prompt.label}</label>
        <Textarea
          placeholder={prompt.placeholder}
          value={responses[prompt.key] || ""}
          onChange={(e) => setResponses(prev => ({ ...prev, [prompt.key]: e.target.value }))}
          rows={3}
          data-testid={`input-${prompt.key}`}
        />
      </div>
    ))}
  </div>
);

/* ── Main page ───────────────────────────────── */

const SexualityExercisesPage = ({ user, logout }) => {
  const { shareData, promptShare, closeSharePrompt } = useSharePrompt();
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [responses, setResponses] = useState({});
  const [tableRows, setTableRows] = useState([{ zone: "", sensations: "", intensity: "", thoughts: "" }]);
  const [journalRows, setJournalRows] = useState([{ situation: "", vitality: "", suffering: "" }]);
  const [entries, setEntries] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchEntries(); }, []);

  const fetchEntries = async () => {
    try {
      const res = await axios.get(`${API}/sexuality-exercises`, { withCredentials: true });
      setEntries(res.data);
    } catch (e) { console.error(e); }
  };

  const hasContent = () => {
    if (selectedExercise?.formType === "table") return tableRows.some(r => r.zone || r.sensations);
    if (selectedExercise?.formType === "journal") return journalRows.some(r => r.situation || r.vitality || r.suffering);
    return Object.values(responses).some(v => v && String(v).trim());
  };

  const saveSession = async () => {
    if (!selectedExercise || !hasContent()) {
      toast.error("Veuillez remplir au moins un champ");
      return;
    }
    setSaving(true);
    try {
      let data = { ...responses };
      if (selectedExercise.formType === "table") data = { ...responses, entries: tableRows.filter(r => r.zone || r.sensations) };
      if (selectedExercise.formType === "journal") data = { ...responses, entries: journalRows.filter(r => r.situation || r.vitality || r.suffering) };

      const res = await axios.post(`${API}/sexuality-exercises`, {
        exercise_type: selectedExercise.id,
        responses: data,
      }, { withCredentials: true });
      toast.success("Exercice enregistre !");
      if (user?.role === "patient") promptShare(res.data, "sexuality_tcc");
      resetForm();
      fetchEntries();
    } catch (e) { toast.error("Erreur lors de l'enregistrement"); }
    setSaving(false);
  };

  const resetForm = () => {
    setSelectedExercise(null);
    setResponses({});
    setTableRows([{ zone: "", sensations: "", intensity: "", thoughts: "" }]);
    setJournalRows([{ situation: "", vitality: "", suffering: "" }]);
  };

  const individualExercises = EXERCISES.filter(e => !e.isCouple);
  const coupleExercises = EXERCISES.filter(e => e.isCouple);

  /* ── Render form ─────── */
  if (selectedExercise) {
    return (
      <DashboardLayout user={user} logout={logout}>
        <div className="max-w-2xl mx-auto space-y-6 pb-12" data-testid="sexuality-exercise-form">
          <div>
            <Button variant="ghost" size="sm" onClick={resetForm} className="mb-1">
              <ArrowLeft className="h-4 w-4 mr-1" /> Retour
            </Button>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${selectedExercise.color} flex items-center justify-center`}>
                <selectedExercise.icon className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-foreground">
                  {selectedExercise.isCouple && <Badge variant="secondary" className="mr-2 text-[10px]">Couple</Badge>}
                  Exercice {selectedExercise.num} : {selectedExercise.name}
                </h1>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">{selectedExercise.description}</p>
          </div>

          <Card className="border-border/40">
            <CardContent className="p-4 sm:p-6 space-y-5">
              {selectedExercise.formType === "table" && <TableForm rows={tableRows} setRows={setTableRows} />}
              {selectedExercise.formType === "journal" && <JournalForm rows={journalRows} setRows={setJournalRows} />}
              {selectedExercise.formType === "levels" && <LevelsForm responses={responses} setResponses={setResponses} />}
              {selectedExercise.formType === "sensate" && <SensateForm responses={responses} setResponses={setResponses} />}
              {selectedExercise.formType === "prompts" && <PromptsForm prompts={selectedExercise.prompts} responses={responses} setResponses={setResponses} />}

              {/* Table + Journal also have a general reflection */}
              {(selectedExercise.formType === "table" || selectedExercise.formType === "journal") && (
                <div>
                  <label className="text-sm font-medium">Reflexion generale</label>
                  <Textarea
                    value={responses.reflection || ""}
                    onChange={(e) => setResponses(p => ({ ...p, reflection: e.target.value }))}
                    placeholder="Qu'avez-vous observe ou appris aujourd'hui ?"
                    rows={3}
                    className="mt-1"
                  />
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button onClick={saveSession} disabled={saving} className="flex-1" data-testid="save-exercise-btn">
                  {saving ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
                <Button variant="outline" onClick={resetForm}>Annuler</Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
      </DashboardLayout>
    );
  }

  /* ── Render list ─────── */
  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-8 pb-12" data-testid="sexuality-exercises-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">TCC Sexualite</h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">
              Exercices de focalisation sensorielle, defusion ACT et travail relationnel
            </p>
          </div>
          {entries.length > 0 && (
            <span className="text-sm text-muted-foreground flex items-center gap-1">
              <History className="w-4 h-4" /> {entries.length} seance(s)
            </span>
          )}
        </div>

        {/* Individual exercises */}
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">Exercices individuels</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {individualExercises.map((exercise) => (
              <Card
                key={exercise.id}
                className="card-hover cursor-pointer transition-all hover:shadow-md"
                onClick={() => setSelectedExercise(exercise)}
                data-testid={`card-${exercise.id}`}
              >
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl ${exercise.color} flex items-center justify-center flex-shrink-0`}>
                      <exercise.icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-heading text-sm font-semibold leading-tight">
                        <span className="text-muted-foreground mr-1">{exercise.num}.</span>
                        {exercise.name}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{exercise.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Couple exercises */}
        <div>
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
            <Users className="h-4 w-4" /> Exercices relationnels (couple)
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {coupleExercises.map((exercise) => (
              <Card
                key={exercise.id}
                className="card-hover cursor-pointer transition-all hover:shadow-md border-pink-200/30 dark:border-pink-800/20"
                onClick={() => setSelectedExercise(exercise)}
                data-testid={`card-${exercise.id}`}
              >
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl ${exercise.color} flex items-center justify-center flex-shrink-0`}>
                      <exercise.icon className="w-5 h-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-heading text-sm font-semibold leading-tight">
                        <span className="text-muted-foreground mr-1">{exercise.num}.</span>
                        {exercise.name}
                      </h3>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{exercise.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* History */}
        {entries.length > 0 && (
          <div>
            <Button variant="ghost" className="mb-3 text-sm" onClick={() => setShowHistory(!showHistory)} data-testid="toggle-history">
              {showHistory ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
              Historique ({entries.length})
            </Button>
            {showHistory && (
              <div className="space-y-3">
                {entries.slice(0, 15).map((entry, i) => {
                  const ex = EXERCISES.find(e => e.id === entry.exercise_type);
                  return (
                    <Card key={i} className="bg-muted/30">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center gap-2">
                            {ex?.isCouple && <Badge variant="secondary" className="text-[9px]">Couple</Badge>}
                            <span className="font-medium text-sm">{ex?.name || entry.exercise_type}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">{new Date(entry.created_at).toLocaleDateString("fr-FR")}</span>
                        </div>
                        {entry.responses && Object.entries(entry.responses).filter(([k]) => k !== "entries").slice(0, 2).map(([k, v]) => (
                          <p key={k} className="text-xs text-muted-foreground truncate">{typeof v === "string" ? v : JSON.stringify(v)}</p>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      <ShareExerciseDialog open={!!shareData} entryId={shareData?.entryId} exerciseType={shareData?.exerciseType} onClose={closeSharePrompt} />
    </DashboardLayout>
  );
};

export default SexualityExercisesPage;
